/**
 * Model Registry - Central registry for all available avatar models.
 *
 * Models are loaded from two sources:
 *   1. LOCAL_REGISTRY: Built-in fallback models (Arc) bundled with the app
 *   2. Remote manifest: Full model library fetched from models.camlinconnect.com.au
 *
 * textureMode:
 *   - 'external': Legacy Default model. Textures loaded separately from /images/*.webp
 *   - 'embedded': Model has baked-in textures in the GLB. No external texture loading needed.
 *   - 'hybrid':   Shenya workaround. Uses useDefaultMaterials (solid skin + mixed textures).
 *
 * blendshapeFormat:
 *   - 'arkit': Standard ARKit 52 blendshape names (eyeBlink_L, jawOpen, mouthSmile_L, etc.)
 *   - 'arkit-51': TomCAT models with 51 ARKit shapes (missing 1, close enough to work)
 */

import { MODEL_ASSET_VERSIONS } from './modelAssetVersions.generated';

/**
 * Default appearance overrides applied when a model first loads.
 * These tint/color the model's baked textures to give each character a unique look.
 * For embedded-texture models, colors are blended with existing textures (not replaced).
 */
export interface ModelDefaultAppearance {
  eyeColor?: string;      // Hex color for iris (e.g. '#4A90D9' for blue)
  hairColor?: string;     // Hex color tint for hair material
  skinColor?: string;     // Hex color tint for skin (subtle blend)
  clothingColor?: string; // Hex color tint for clothing
}

export interface ModelConfig {
  id: string;
  name: string;
  url: string;
  textureMode: 'external' | 'embedded' | 'hybrid';
  blendshapeFormat: 'arkit' | 'arkit-51' | 'custom';
  /**
   * Skeleton rig family. Authoritative replacement for the historical
   * `isXandraUE5AvatarUrl()` URL-substring guard in `Avatar.tsx`.
   *
   *   - 'ue5' — UE5 MetaHuman rig (Xandra3D F4/M4 and the renamed default
   *     Arc, plus their asset-time skeleton-merged variants). These rigs
   *     need MetaHuman-specific morph corrections AND must SKIP the legacy
   *     FBX skeletal idle clip (the FBX clip's rest poses are incompatible
   *     with the UE5 skeleton and drive bones into a wrong-rest pose on
   *     load — torn collar / head tilt).
   *   - 'fbx' — legacy Daz/Mixamo-style rig (Sam's `/model.glb`, the
   *     GymGirl meshes). These USE the FBX skeletal idle clip.
   *
   * WHY this exists (do not regress to URL substrings): the May 17 2026 Arc
   * regression was a `m4-male.glb` → `arc.glb` rename that slipped through
   * the `u.includes('m4-')` guard, so Arc silently started getting the FBX
   * idle clip on its UE5 skeleton. Declaring `skeletonType` on the registry
   * entry makes the classification rename-proof — `Avatar.tsx` resolves the
   * model by URL and trusts this field, falling back to the substring
   * heuristic only for URLs not present in the registry.
   *
   * NOTE: this is the rig-FAMILY axis only. Armature COUNT (single merged
   * vs. multi-skeleton) is a separate concern handled by `gestureOverrides`
   * + the asset-pipeline readiness gates — do not overload this field with
   * a 'single'/'multi' value.
   *
   * When unset, `Avatar.tsx` falls back to the URL-substring heuristic, so
   * annotating every model is not strictly required — but annotating the
   * UE5 family is what makes the rename-proofing real.
   */
  skeletonType?: 'ue5' | 'fbx';
  /** Number of expected morph targets (for validation) */
  expectedMorphTargets?: number;
  /** Small portrait image for model picker grids (~24KB JPEG) */
  thumbnail?: string;
  /** Large portrait image for preview panels (~200-300KB JPEG) */
  portrait?: string;
  /** CSS transform to zoom/reposition portrait in cards (e.g. 'scale(1.2) translateY(-8%)') */
  thumbnailZoom?: string;
  /** Which agent this model is assigned to (if any) */
  agentId?: string;
  /** Brief description for the model picker */
  description?: string;
  /** Source of the model (for attribution/tracking) */
  source?: string;
  /** Whether the model is ready for production use (false = hidden from picker) */
  ready?: boolean;
  /** Optional scale override (default 1.0). Use for models at different unit scales. */
  scale?: number;
  /** Optional Y position offset to align face with camera */
  positionY?: number;
  /** Optional default TTS voice ID (Azure voice name) for this model */
  voiceId?: string;
  /** Default appearance colors applied on model load (eye, hair, skin tints) */
  defaultAppearance?: ModelDefaultAppearance;
  /** URL for low-resolution GLB variant (~1-3MB) for fast initial loading */
  lowresUrl?: string;
  /**
   * Per-model multiplier on the global MOOD_STATES intensity values, applied
   * at mood-overlay time (`moodAnimator.startMoodOverlay`). Default 1 (no
   * change). Use values <1 to attenuate exaggerated mood reactions on a model
   * whose rest pose amplifies mood blendshapes more than the GymGirl-class
   * meshes the global MOOD_STATES were tuned against.
   *
   * Why per-model and not global: changing MOOD_STATES weights affects every
   * model. Pulling Sam's negative-sentiment lower-lip down is the right fix on
   * Sam (legacy default mesh exaggerates frown blendshapes) but would make
   * Cara/Blake/Sienna under-react.
   *
   * Sam ships with 0.55 (May 11 2026, Slice 6 of the avatar-fixes plan).
   */
  moodIntensity?: number;
  /**
   * Extra URL paths that should resolve to this model (filename aliases).
   * Sam ships as `/model.glb` locally and `/models/default.glb` on the library.
   */
  urlAliases?: string[];
  /**
   * ARKit morph names this model must pin to 0 during mood overlay.
   * Sam's CC rest pose maps close / press / shrugLower / roll as
   * lower-lip-over-upper. Pin those to 0 (do not merely drop them — idle
   * mixer tracks would keep driving the invert) instead of editing global
   * MOOD_STATES (which would flatten concern on Cara / GymGirl).
   * Do NOT pin mouthFrown on Sam — a small frown is the worried downturn;
   * close/press/shrug/roll are the invert family.
   */
  omitMoodBlendshapes?: string[];
  /**
   * Per-mood blendshape / intensity overrides merged on top of MOOD_STATES
   * before omit-pinning. Use this for a Sam-safe concerned recipe (stronger
   * brows, reduced frown) without editing the global mood table.
   */
  moodOverrides?: Record<string, { blendshapes?: Record<string, number>; intensity?: number; enabled?: boolean; absoluteBlendshapes?: boolean }>;
  /**
   * Mesh primitives whose material name matches one of these substrings will
   * be hidden at load time. Use this when a GLB ships duplicate / unused
   * sub-meshes that overlap with the visible geometry and produce z-fighting
   * or stray geometry artefacts.
   *
   * Matching is case-insensitive substring against `material.name` on each
   * primitive (or on the mesh-level material if the renderer collapsed
   * primitives). The mesh stays in the scene graph (so its skeleton and
   * morph targets are still available — important for face primitives
   * sharing morph dictionaries) but `mesh.visible = false` is set so it
   * doesn't render.
   *
   * Arc (`arc.glb`) ships `MI_Ryan_Top` as a stray duplicate shirt
   * primitive on `SK_M4_Torso_Modern_Levi` — overlapping the actual
   * `MI_Levi_Shirt` and producing a torn-collar artefact. Hide it.
   * (May 12 2026, Arc clothing fix.)
   */
  hideMaterials?: string[];
  /**
   * Per-model gesture allow/deny overrides. Keys are GestureType
   * (`'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back'`).
   * `true` = force enabled (skeletal + face). `false` = face-only on this
   * model (skeletal skipped — use for rigs where bone motion tears clothes).
   * Undefined keys fall back to GLOBAL_GESTURE_DEFAULTS in Avatar.tsx
   * (`wave` is globally off unless a model sets `wave: true`).
   *
   * GLOBAL_GESTURE_DEFAULTS as of May 12 2026:
   *   - `wave`: globally **disabled**. Cara was the last opt-in; her wave
   *     tears clothes (Keith, Aug 13 2026 enamel18) so she is opted out too.
   *   - `nod`/`shake`/`lean-forward`/`lean-back`/`shrug`: globally enabled.
   *     Models whose hair / clothes / body are bound to a non-shared
   *     skeleton opt out via `gestureOverrides: { 'lean-forward': false, ... }`
   *     because the world-space rotation pivots the head bone but the
   *     duplicated skeletons in the hair/clothes meshes don't follow.
   *
   * Current `lean-forward`/`lean-back`/`shrug` opt-outs (Aug 13 2026):
   * `f4-riley` (Mia), `f4-naomi`, `f4-ashley`, `f4-kiera`. Cara (`f4-xandra`)
   * keeps reduced-amplitude skeletal shrug/leans (~32%) rather than face-only
   * gating. `f4-shay` (Nicola) opts out of `shrug` only, via the morph-allowlist
   * route.
   *
   * These four were opted back out on Jul 28 2026 after production visual QA
   * found real tearing at gesture peak. The Jun 13 2026 commit that had
   * re-enabled them relied on a skinning-integrity probe that was later
   * retracted for measuring a fixed point rather than the mesh (commit
   * 0490cc6). Do NOT re-enable on probe readings alone — a green v2 probe is
   * necessary but not sufficient; pair it with a visual pass captured at
   * gesture PEAK (not at rest), and check `modelRegistry.gestures.test.ts`,
   * which locks the current policy deliberately so a re-enable cannot land
   * silently.
   *
   * Slice 8 of the avatar-fixes plan, May 12 2026.
   */
  gestureOverrides?: Partial<Record<
    'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
    boolean
  >>;
  /**
   * Scales procedural skeletal gesture keyframe angles (1.0 = default).
   * Use < 1 on models whose embedded layered clothing (jacket + shirt
   * primitives on one torso mesh) tears when the upper spine bends hard.
   * Does not affect face-only blendshape gestures.
   */
  gestureSkeletalIntensity?: number;
  /**
   * Per-gesture multiplier on top of `gestureSkeletalIntensity` (1.0 = default).
   * Arc shrug tears the embedded jacket/shirt stack — use a lower shrug factor.
   */
  gestureSkeletalIntensityByGesture?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      number
    >
  >;
  /**
   * Bone roles to skip for skeletal gestures on this model. Arc's default
   * outfit (MI_Levi_Shirt + MI_Ryan_Top on one torso mesh) tears when the
   * upper chest bone rotates — lean from spine+neck only.
   */
  gestureSuppressBoneRoles?: Array<
    'rightShoulder' | 'leftShoulder' | 'rightUpperArm' | 'leftUpperArm' |
    'rightForeArm' | 'leftForeArm' | 'rightHand' | 'leftHand' |
    'spine' | 'chest' | 'neck' | 'head'
  >;
  /** Per-gesture bone suppress (merged with `gestureSuppressBoneRoles`). */
  gestureSuppressBoneRolesByGesture?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      Array<
        'rightShoulder' | 'leftShoulder' | 'rightUpperArm' | 'leftUpperArm' |
        'rightForeArm' | 'leftForeArm' | 'rightHand' | 'leftHand' |
        'spine' | 'chest' | 'neck' | 'head'
      >
    >
  >;
  /** Per-gesture blendshape track intensity for GestureController.play. */
  gestureFaceIntensityByGesture?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      number
    >
  >;
  /**
   * Skip JSON headRotation/headPosition for a gesture (morph-only). Stops
   * collar tear on Arc when shake JSON rotates the head bone.
   *
   * IMPORTANT — this is the OTHER HALF of `gestureOverrides: { g: false }`.
   * The head Euler write lives in `applyGestureMorphFrame` on the FACE path in
   * `Avatar.tsx`, which ALWAYS runs ("Always play the blendshape (face)
   * component"). `gestureOverrides: false` suppresses only the skeletal/torso
   * component, so a model set to face-only STILL rotates and translates its
   * head bone. On a multi-skeleton rig whose collar / hair meshes are bound to
   * duplicated skeletons that don't follow the head, that head-only motion is
   * on its own enough to tear the neck seam.
   *
   * Verified on the live runtime host Jul 28 2026: Mia (`f4-riley`) with
   * `gestureOverrides: { shrug: false }` alone still fragmented her turtleneck
   * collar at shrug peak, while idle frames either side stayed clean. Pairing
   * the override with `gestureDisableHeadEuler` is the complete recipe — the
   * same pairing Arc uses for shake and Nicola (`f4-shay`) uses for shrug.
   *
   * So: to genuinely stop a tearing gesture on a multi-skel rig you need BOTH
   * `gestureOverrides: { g: false }` AND `gestureDisableHeadEuler: { g: true }`.
   * `modelRegistry.gestures.test.ts` asserts the pair stays together.
   */
  gestureDisableHeadEuler?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      boolean
    >
  >;
  /**
   * Scale JSON headRotation/headPosition from gesture blend data (1 = full).
   * Use on face-only rigs where a little head tilt reads clearly but full
   * rotation tears the embedded collar (Arc).
   */
  gestureHeadEulerScale?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      number
    >
  >;
  /**
   * Scale mouth/jaw/chin morph keys during gestures (1 = default). Arc's
   * head mesh verts at the collar line shear the layered jacket when these
   * shapes peak too high.
   */
  gestureLowerFaceMorphScale?: number;
  gestureLowerFaceMorphScaleByGesture?: Partial<
    Record<
      'wave' | 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back',
      number
    >
  >;
  /**
   * Embedded-outfit rigs (jacket+shirt on one torso mesh): shrug uses only
   * these ARKit morph keys on the head mesh. Pair with `gestureOverrides:
   * { shrug: false }` so shoulders/head bones do not run.
   */
  gestureShrugMorphAllowlist?: string[];
  /** Boost allowlisted shrug morphs (typical 1.75–2.0 on Arc / Nicola). */
  gestureShrugMorphBoost?: number;
}

/** Upper-face morph keys that do not shear embedded collar geometry. */
export const EMBEDDED_OUTFIT_SHRUG_MORPH_ALLOWLIST = [
  'browInnerUp',
  'browOuterUpLeft',
  'browOuterUpRight',
  'eyeSquintLeft',
  'eyeSquintRight',
  'eyeWideLeft',
  'eyeWideRight',
] as const;

/**
 * Concerned mood for F4/M4 meshes where global mouthClose + mouthPress
 * ride the lower lip up and pinch. Keep brows worried; pin invert-family
 * mouth keys to 0 on this mood only (do not copy Sam's omit list, which
 * would also flatten viseme-owned morphs on every other mood).
 */
const CONCERNED_BROWS_NO_LIP_PINCH: NonNullable<ModelConfig['moodOverrides']> = {
  concerned: {
    blendshapes: {
      mouthClose: 0,
      mouthPress_L: 0,
      mouthPress_R: 0,
      mouthShrugLower: 0,
      mouthRollLower: 0,
      mouthFrown_L: 0.18,
      mouthFrown_R: 0.18,
      browDown_L: 0.70,
      browDown_R: 0.70,
      browInnerUp: 0.52,
      eyeSquint_L: 0.30,
      eyeSquint_R: 0.30,
    },
  },
};

export type AvatarBackgroundVariant = 'desktop' | 'tablet' | 'mobile' | 'thumbnail';

export interface AvatarBackgroundConfig {
  id: string;
  name: string;
  /**
   * Stable scene family slug (e.g. 'service-lounge'). Multiple backgrounds with
   * the same family are alternative looks of the same scene that the UI groups
   * into a single tile with left/right variant arrows.
   */
  family?: string;
  /**
   * 1-based variant index within the family (1, 2, 3, ...). Use together with
   * `family` to order alternative looks. Single-variant scenes have variantIndex 1.
   */
  variantIndex?: number;
  category?: string;
  description?: string;
  agentId?: string;
  defaultForModelIds?: string[];
  url: string;
  variants?: Partial<Record<AvatarBackgroundVariant, string>>;
  width?: number;
  height?: number;
  focalPoint?: { x: number; y: number };
  ready?: boolean;
}

/** Remote manifest schema */
interface ModelManifest {
  version: number;
  models: ModelConfig[];
  backgrounds?: AvatarBackgroundConfig[];
}

// ---------------------------------------------------------------------------
// Remote Model Library
// ---------------------------------------------------------------------------

const CAMLIN_TLDS = ['camlinconnect.com.au', 'camlinconnect.co.uk', 'camlinconnect.com'];
const DEFAULT_MODEL_LIBRARY_URL = 'https://models.camlinconnect.com.au';
const DEFAULT_AVATAR_RUNTIME_URL = 'https://avatar.camlinconnect.com.au';

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function readWindowUrlOverride(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as Record<string, unknown>)[key];
  if (typeof candidate !== 'string' || !candidate.trim()) {
    return null;
  }
  return trimTrailingSlash(candidate.trim());
}

function isLocalDevelopmentHostname(hostname: string): boolean {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '[::1]'
    || hostname.endsWith('.local');
}

function joinAssetUrl(base: string, url: string): string {
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${trimTrailingSlash(base)}${normalizedPath}`;
}

function resolveCamlinServiceBaseUrl(
  subdomain: 'models' | 'avatar',
  fallbackUrl: string,
  overrideKey: string
): string {
  if (typeof window === 'undefined') return '';

  const override = readWindowUrlOverride(overrideKey);
  if (override) {
    return override;
  }

  const hostname = window.location.hostname;
  for (const tld of CAMLIN_TLDS) {
    if (hostname === tld || hostname.endsWith(`.${tld}`)) {
      return `https://${subdomain}.${tld}`;
    }
  }

  // Local development keeps relative asset paths so standalone avatar workspaces
  // can continue serving assets from their own public folders.
  if (isLocalDevelopmentHostname(hostname)) {
    return '';
  }

  // External hosts (like Living Notes) should consume the shared Camlin asset
  // services instead of assuming the embedding app serves avatar assets itself.
  return fallbackUrl;
}

/**
 * Base URL for the avatar model library service.
 * In production: https://models.camlinconnect.com.au
 * In dev: falls back to local models if unavailable.
 *
 * Override with `window.__CAMLIN_MODEL_LIBRARY_URL__` when needed.
 */
export function getModelLibraryBaseUrl(): string {
  const base = resolveCamlinServiceBaseUrl('models', DEFAULT_MODEL_LIBRARY_URL, '__CAMLIN_MODEL_LIBRARY_URL__');
  if (base) {
    return base;
  }

  // Registry models are owned by the model-library service, not by the
  // standalone avatar Vite dev server. Keeping this as a localhost-relative
  // URL makes /models/f4-shay-lowres.glb resolve to Vite's index.html, which
  // then crashes GLTFLoader with "Unexpected token '<'".
  if (typeof window !== 'undefined' && isLocalDevelopmentHostname(window.location.hostname)) {
    return DEFAULT_MODEL_LIBRARY_URL;
  }

  return base;
}

/**
 * Base URL for shared model-library assets that are not bundled into the local
 * dev host, such as generated eye-variant textures.
 *
 * Unlike `getModelLibraryBaseUrl()`, this intentionally falls back to the
 * deployed models service even on localhost so local `/live` can still load
 * shared generated assets from the canonical model library.
 */
export function getSharedModelLibraryBaseUrl(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_MODEL_LIBRARY_URL;
  }

  const override = readWindowUrlOverride('__CAMLIN_MODEL_LIBRARY_URL__');
  if (override) {
    return override;
  }

  const hostname = window.location.hostname;
  for (const tld of CAMLIN_TLDS) {
    if (hostname === tld || hostname.endsWith(`.${tld}`)) {
      return `https://models.${tld}`;
    }
  }

  return DEFAULT_MODEL_LIBRARY_URL;
}

/**
 * Base URL for shared avatar runtime assets (textures, HDRs, Draco, animations).
 *
 * Override with `window.__CAMLIN_AVATAR_RUNTIME_URL__` when needed.
 */
export function getAvatarRuntimeBaseUrl(): string {
  return resolveCamlinServiceBaseUrl('avatar', DEFAULT_AVATAR_RUNTIME_URL, '__CAMLIN_AVATAR_RUNTIME_URL__');
}

/**
 * Resolve a runtime asset URL (textures, HDRs, Draco decoders, FBX files)
 * against the shared avatar host for external consumers.
 */
export function resolveAvatarRuntimeAssetUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  const base = getAvatarRuntimeBaseUrl();
  if (!base) return url;
  return joinAssetUrl(base, url);
}

/**
 * Resolve shared model-library assets that should come from the deployed models
 * service even during local development.
 */
export function resolveSharedModelLibraryAssetUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  return joinAssetUrl(getSharedModelLibraryBaseUrl(), url);
}

/**
 * Append the content-hash cache buster (`?v=<hash>`) to a model URL.
 *
 * Model GLBs live on FIXED URLs behind a year-long immutable cache; without
 * a byte-derived buster, clients (iOS Safari especially — see
 * .cursor/rules/ios-cache-busting.mdc) keep serving a stale model after an
 * asset fix is deployed. The hash map is generated from the GLB bytes by
 * scripts/generate-model-asset-versions.mjs; unknown paths (e.g. models that
 * only exist in the remote manifest) pass through unchanged.
 */
export function appendModelAssetVersion(url: string): string {
  if (!url || url.startsWith('data:')) return url;
  if (/[?&]v=/.test(url)) return url;

  let pathname = url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      pathname = new URL(url).pathname;
    } catch {
      return url;
    }
  } else {
    pathname = url.split('?')[0].split('#')[0];
  }

  const version = MODEL_ASSET_VERSIONS[pathname];
  if (!version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${version}`;
}

/**
 * Resolve a model URL to an absolute URL using the model library base.
 * Relative URLs (e.g. /models/arc.glb) are prefixed with the library base.
 * Absolute URLs (https://...) keep their origin unchanged.
 * All resolved model URLs carry the content-hash `?v=` cache buster when the
 * model's bytes are known — see appendModelAssetVersion.
 */
export type AvatarQuality = 'hd' | 'sd';

export function resolveModelUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return appendModelAssetVersion(url);
  }

  // These models are bundled locally with builder/avatar apps, not on the CDN:
  // - Arc fallback (/model.glb)
  // - Shenya (/shenya-fullbody-ue.glb)
  const localModels = ['/model.glb', '/shenya-fullbody-ue.glb'];
  if (localModels.includes(url)) {
    const runtimeBase = getAvatarRuntimeBaseUrl();
    if (!runtimeBase) return appendModelAssetVersion(url);
    return appendModelAssetVersion(joinAssetUrl(runtimeBase, url));
  }

  const base = getModelLibraryBaseUrl();
  if (!base) return appendModelAssetVersion(url);
  return appendModelAssetVersion(joinAssetUrl(base, url));
}

/**
 * Resolve the model URL based on quality preference.
 * 'sd' uses the lowresUrl variant if available, falling back to the full URL.
 */
export function resolveModelUrlForQuality(model: ModelConfig, quality: AvatarQuality = 'hd'): string {
  if (quality === 'sd' && model.lowresUrl) {
    return resolveModelUrl(model.lowresUrl);
  }
  return resolveModelUrl(model.url);
}

// ---------------------------------------------------------------------------
// Local Fallback Registry (bundled with the app)
// ---------------------------------------------------------------------------

const LOCAL_REGISTRY: ModelConfig[] = [
  // === Arc — Platform default (male, Xandra3D M4 mesh; file renamed from
  // m4-male.glb -> arc.glb May 11 2026 to remove the Sam confusion) ===
  {
    id: 'default',
    name: 'Arc',
    url: '/models/arc.glb',
    lowresUrl: '/models/arc-lowres.glb',
    skeletonType: 'ue5',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/arc-sm.jpg',
    portrait: '/portraits/arc-lg.jpg',
    agentId: 'arc',
    description: 'AI Architect - Camlin Default avatar (male)',
    source: 'Xandra3D (M4)',
    ready: true,
    scale: 0.01,
    positionY: -0.12,
    voiceId: 'en-AU-WilliamNeural',
    defaultAppearance: { eyeColor: '#5B7A3A' },
    // arc.glb v10 (May 28 2026) splits the old combined torso into three meshes:
    //   SK_M4_BodySkin, SK_M4_Torso_Levi_Shirt, SK_M4_Torso_Ryan_Top
    // (was one SK_M4_Torso_Modern_Levi node with three material primitives)
    // The May 12 2026 fix incorrectly classified MI_Ryan_Top as a
    // "stray duplicate" and hid it via `hideMaterials: ['MI_Ryan_Top']`.
    // That removed the white t-shirt entirely — Arc shipped wearing
    // only a dark jacket with bare skin underneath at the collar
    // (the "missing tshirt" Keith reported May 17 2026 once the
    // 28.7 MB master GLB was re-deployed). The two primitives are
    // an intentional layered outfit; the z-fighting at the collar
    // is a minor authoring artefact and is the lesser evil compared
    // to a missing garment. Removed May 17 2026.
    // Slice 12 (May 15 2026): gestures share ONE base quaternion
    // across the 5 duplicate-skeleton meshes so spine_01 rotation
    // is consistent. Kept enabled.
    //
    // Slice 18 (May 24 2026): re-add the Slice 8 opt-outs. The
    // Slice 12 re-enable was premised on the shared-base-quaternion
    // fix working universally, but Slice 16 (full-body sibling
    // sync) was REVERTED in Slice 17 (May 16 2026) after it
    // produced Naomi-face-backwards + Arc-distortion regressions.
    // Per Slice 17's own commit message: "Multi-skel BODY gestures
    // (lean-forward / lean-back / shrug on F4/M4 rigs) remain
    // partially broken: head moves, body / hair / clothes stay."
    // The Slice 12 comment that originally re-enabled these
    // explicitly said "if regressions surface in production, re-add
    // the gestureOverrides block to disable." User confirmed
    // regressions visible on Arc (May 24 2026), so we re-disable
    // until a universal rig fix lands (Slice 19 design under
    // review). Face-only fallback (blendshapes) still fires.
    //
    // May 28 2026: restore the calmer pre-v10 gesture tuning. Arc nod/shake
    // read acceptably at ~50% skeletal strength; broad platform shrug/lean
    // boosts and the v10b asset pass regressed the jacket/t-shirt.
    gestureFaceIntensityByGesture: {
      shrug: 1.15,
      shake: 0.7,
      nod: 1.15,
    },
    gestureHeadEulerScale: {
      nod: 0.55,
      shrug: 0.4,
      shake: 0.5,
    },
    gestureLowerFaceMorphScale: 0.85,
    gestureSkeletalIntensity: 0.5,
    gestureSkeletalIntensityByGesture: {
      nod: 0.55,
      shake: 0.5,
      shrug: 0.45,
      'lean-forward': 0.65,
      'lean-back': 0.65,
    },
    gestureSuppressBoneRoles: ['chest'],
  },

  // === GymGirl models (CGTrader - Ida Faber) ===
  {
    id: 'gymgirl-01',
    name: 'Sienna',
    url: '/models/gymgirl-01.glb',
    skeletonType: 'fbx',
    lowresUrl: '/models/gymgirl-01-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/sienna-sm.jpg',
    portrait: '/portraits/sienna-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    agentId: 'sienna',
    description: 'Operations Assistant - realistic, ponytail',
    source: 'CGTrader (Ida Faber)',
    // Without a voiceId the TTS layer falls back to the platform default
    // (male en-AU-WilliamNeural) — every female model must set one explicitly.
    voiceId: 'en-AU-NatashaNeural',
    ready: true,
    defaultAppearance: { eyeColor: '#6B4423' }, // warm brown eyes
  },
  {
    id: 'gymgirl-02',
    name: 'Petra',
    url: '/models/gymgirl-02.glb',
    skeletonType: 'fbx',
    lowresUrl: '/models/gymgirl-02-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/petra-sm.jpg',
    portrait: '/portraits/petra-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    agentId: 'petra',
    description: 'Planner - realistic, variant 2',
    source: 'CGTrader (Ida Faber)',
    voiceId: 'en-GB-SoniaNeural',
    ready: true,
    defaultAppearance: { eyeColor: '#4A90D9' }, // bright blue eyes
  },
  {
    id: 'gymgirl-03',
    name: 'Cora',
    url: '/models/gymgirl-03.glb',
    skeletonType: 'fbx',
    lowresUrl: '/models/gymgirl-03-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/cora-sm.jpg',
    portrait: '/portraits/cora-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    agentId: 'cora',
    description: 'Optimizer - realistic, variant 3',
    source: 'CGTrader (Ida Faber)',
    voiceId: 'en-US-AriaNeural',
    ready: true,
    defaultAppearance: { eyeColor: '#5C8A4D' }, // forest green eyes
  },
  {
    id: 'gymgirl-04',
    name: 'Harper',
    url: '/models/gymgirl-04.glb',
    skeletonType: 'fbx',
    lowresUrl: '/models/gymgirl-04-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/harper-sm.jpg',
    portrait: '/portraits/harper-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    agentId: 'harper',
    description: 'Voice Specialist - realistic, variant 4',
    source: 'CGTrader (Ida Faber)',
    voiceId: 'en-AU-CarlyNeural',
    ready: true,
    defaultAppearance: { eyeColor: '#8B7355' }, // hazel eyes
  },
  {
    id: 'gymgirl-05',
    name: 'Blake',
    url: '/models/gymgirl-05.glb',
    skeletonType: 'fbx',
    lowresUrl: '/models/gymgirl-05-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/blake-sm.jpg',
    portrait: '/portraits/blake-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    agentId: 'blake',
    description: 'Flow Designer - realistic, variant 5',
    source: 'CGTrader (Ida Faber)',
    voiceId: 'en-US-JennyNeural',
    ready: true,
    defaultAppearance: { eyeColor: '#607D8B' }, // steel gray eyes
  },

  // === Sam — Female validator avatar on the legacy default mesh ===
  // Historical note: Sam's id was `'m4-male'` from Feb 2026 - May 2026 (a
  // confusing artefact of an early M4-series naming convention, since the
  // unrelated Arc avatar uses the M4 male mesh). Renamed to `'sam'` on
  // May 11 2026 to remove the M4-male / Sam-female confusion. The
  // migration script `apps/runtime-service/scripts/migrate-sam-id.mjs`
  // rewrites any persisted `'m4-male'` avatarModelId / linkedAvatarModelId
  // values across the tenant store and flow store. Sam's GLB is the
  // original Camlin avatar mesh (`/model.glb` locally, `/models/default.glb`
  // on the deployed library).
  {
    id: 'sam',
    name: 'Sam',
    url: '/model.glb',
    urlAliases: ['/models/default.glb'],
    lowresUrl: '/models/default-lowres.glb',
    skeletonType: 'fbx',
    textureMode: 'external',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/sam-sm.jpg',
    portrait: '/portraits/sam-lg.jpg',
    agentId: 'sam',
    description: 'Validator - female avatar (legacy default mesh)',
    source: 'Custom (legacy)',
    ready: true,
    voiceId: 'en-AU-NatashaNeural',
    // Sam's legacy default mesh has a softer rest pose than the GymGirl-class
    // meshes the global MOOD_STATES weights were tuned against; the same
    // negative-sentiment frown blendshapes that read as gentle on Cara/Blake/
    // Sienna read as exaggerated on Sam (lower lip rises over the top lip).
    // Cap mood overlay strength at 55% on Sam only. See Slice 6 of the
    // avatar-fixes plan and ModelConfig.moodIntensity JSDoc.
    moodIntensity: 0.55,
    // Invert family on this CC rest pose: close / press / shrug / roll raise
    // the lower lip over the upper. Pin those to 0. Keep a small frown (via
    // moodOverrides.concerned) so concerned still reads worried.
    omitMoodBlendshapes: [
      'mouthClose',
      'mouthPress',
      'mouthLowerDown',
      'mouthShrugLower',
      'mouthShrugUpper',
      'mouthRollLower',
      'mouthRollUpper',
      'Basis.001',
    ],
    moodOverrides: {
      concerned: {
        // Values are on-mesh weights (skip Sam's 0.55 global scale) so
        // brows actually knit. Keep frown modest so the lip does not invert.
        absoluteBlendshapes: true,
        blendshapes: {
          browDown_L: 0.72,
          browDown_R: 0.72,
          browInnerUp: 0.58,
          eyeSquint_L: 0.42,
          eyeSquint_R: 0.42,
          mouthFrown_L: 0.16,
          mouthFrown_R: 0.16,
        },
      },
    },
  },
  {
    id: 'm4-joel',
    name: 'Joel',
    url: '/models/m4-joel.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/m4-joel-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/joel-sm.jpg',
    portrait: '/portraits/joel-lg.jpg',
    description: 'Male avatar - Joel jacket, casual pants, black hair',
    source: 'Xandra3D (M4)',
    ready: true,
    scale: 0.01,
    positionY: -0.12,
    voiceId: 'en-US-GuyNeural',
    defaultAppearance: { eyeColor: '#3E2723' }, // dark brown eyes
    // Jun 13 2026: lean RE-ENABLED at 0.65 skeletal intensity. The May 16
    // merge log shows this M4 rig collapsed to a single armature; the earlier
    // "broken bind poses" disable was based on a rotation-CONFOUNDED probe
    // (AABB-center seam + AABB-diagonal growth read a legitimate lean as a
    // "tear"). The corrected probe (true min-gap seam) is the guardrail.
    // The Slice-18 "multi-skel hair-left-behind" opt-out predates the merge
    // and is obsolete. Verified via the Avatar Animation Tester.
    //
    // FOLLOW-UP (Jul 28 2026): the probe cited above was retracted (commit
    // 0490cc6 — it measured a fixed point, not the mesh), and the four F4
    // models re-enabled by the same Jun 13 commit were confirmed tearing in
    // production and rolled back. Joel was left ENABLED because the 0.65
    // skeletal damping below is a real mitigation the F4 models never had, and
    // because he was NOT part of the failing visual QA set — but he is also
    // UNTESTED at gesture peak. Do not treat this as cleared: schedule a visual
    // pass (and a v2-probe reading) before relying on Joel's leans.
    gestureSkeletalIntensityByGesture: {
      'lean-forward': 0.65,
      'lean-back': 0.65,
    },
    // Aug 13 2026: global concerned mouthClose/press pinches Joel's lower
    // lip the same invert class as Sam. Override this mood only — viseme
    // still owns those morphs. Do not copy Sam's omitMoodBlendshapes list.
    moodOverrides: { ...CONCERNED_BROWS_NO_LIP_PINCH },
  },
  {
    id: 'm4-ryan',
    name: 'Ryan',
    url: '/models/m4-ryan.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/m4-ryan-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/ryan-sm.jpg',
    portrait: '/portraits/ryan-lg.jpg',
    description: 'Male avatar - Ryan top, casual pants, blonde hair',
    source: 'Xandra3D (M4)',
    ready: true,
    scale: 0.01,
    positionY: -0.12,
    voiceId: 'en-IE-ConnorNeural',
    defaultAppearance: { eyeColor: '#4A90D9' }, // blue eyes (matches blonde)
  },
  {
    id: 'm4-jason',
    name: 'Jason',
    url: '/models/m4-jason.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/m4-jason-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 52,
    thumbnail: '/portraits/jason-sm.jpg',
    portrait: '/portraits/jason-lg.jpg',
    description: 'Male avatar - crew neck jumper, skinny jeans, sandy hair',
    source: 'Xandra3D (M4)',
    ready: true,
    scale: 0.01,
    positionY: -0.12,
    voiceId: 'en-GB-RyanNeural',
    defaultAppearance: { eyeColor: '#8B7355' }, // hazel eyes (matches sandy hair)
    // Jun 13 2026: lean RE-ENABLED at 0.65 skeletal intensity (same M4 rig
    // as Joel). The earlier disable was based on a rotation-confounded probe;
    // the corrected min-gap probe is the guardrail. See Joel above.
    //
    // FOLLOW-UP (Jul 28 2026): same status as Joel — the justifying probe was
    // retracted (commit 0490cc6), Jason kept his leans only because the 0.65
    // damping is a real mitigation and he was not in the failing visual QA set.
    // UNTESTED at gesture peak; needs a visual pass before being called clear.
    gestureSkeletalIntensityByGesture: {
      'lean-forward': 0.65,
      'lean-back': 0.65,
    },
  },

  // === F4 Female (Xandra3D) ===
  {
    id: 'f4-xandra',
    name: 'Cara',
    url: '/models/f4-xandra.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-xandra-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/cara-sm.jpg',
    portrait: '/portraits/cara-lg.jpg',
    agentId: 'cara',
    description: 'Contact Centre Assistant - modern casual, long hair',
    source: 'Xandra3D (F4)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-AU-NatashaNeural',
    defaultAppearance: { eyeColor: '#4CAF50', clothingColor: '#2D3748' }, // green eyes, dark charcoal top
    // Aug 13 2026 evening: Cara wave TEARS clothes (Keith). Wave is
    // globally off; do not opt her back in. Lean/shrug stay reduced
    // skeletal (~32%) with no chest follow-through on leans.
    // Do not copy Sam's concerned omit list.
    gestureOverrides: {
      wave: false,
    },
    gestureSkeletalIntensityByGesture: {
      shrug: 0.32,
      'lean-forward': 0.32,
      'lean-back': 0.32,
    },
    gestureSuppressBoneRolesByGesture: {
      'lean-forward': ['chest'],
      'lean-back': ['chest'],
    },
    gestureHeadEulerScale: {
      shrug: 0.45,
      'lean-forward': 0.4,
      'lean-back': 0.4,
    },
    // Same invert-class concerned lip as Nicola/Joel: brows worried, no pinch.
    // Cara carried a per-model `celebration` override of 0.90 smile / 0.35 jaw
    // until Aug 16 2026. The global now IS 0.90 / 0.35, so it was redundant —
    // and it had never worked as described: its jawOpen of 0.35 sat BELOW the
    // then-global 0.40, so the override meant to widen her grin silently
    // narrowed her jaw instead.
    moodOverrides: { ...CONCERNED_BROWS_NO_LIP_PINCH },
  },
  {
    id: 'f4-ashley',
    name: 'Ashley',
    url: '/models/f4-ashley.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-ashley-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/ashley-sm.jpg',
    portrait: '/portraits/ashley-lg.jpg',
    description: 'Female avatar - leather jacket, skirt, mid-length hair',
    source: 'Xandra3D (F4 Modern Girls)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-US-AriaNeural',
    defaultAppearance: { eyeColor: '#6B4423', hairColor: '#5C3A28' }, // brown eyes, warm chestnut hair
    // Jul 28 2026 ROLLBACK: lean + shrug RE-DISABLED (skeletal off, face-only).
    //
    // The Jun 13 2026 re-enable justified itself with a "corrected" min-gap
    // skinning-integrity probe that concluded the original tear reports were
    // false positives. That probe was itself wrong — it measured a FIXED point
    // rather than the mesh (retracted by the v2 probe rewrite, commit 0490cc6),
    // so its "clean" readings on this rig were meaningless.
    //
    // Visual QA against the live production runtime host (rev 62, bundle
    // main-DFwSNaxf.js) confirmed REAL tearing at gesture peak: shrug drives a
    // flesh-coloured shard through the jacket at the shoulder. Lean read as
    // marginal rather than clean-clean, and we are not splitting hairs over a
    // marginal call on a production surface — both leans go off with the shrug.
    // Cara (f4-xandra), which was never disabled, stayed clean under the
    // identical test, so this is rig-specific and not a probe/test artefact.
    //
    // Re-enabling requires a fresh evaluation with the v2 probe PLUS a visual
    // pass at gesture peak. See the gesture-policy lock test in
    // `modelRegistry.gestures.test.ts` before changing these values.
    gestureOverrides: {
      'wave': false,
      'lean-forward': false,
      'lean-back': false,
      shrug: false,
    },
    // Required alongside the override above — the override alone leaves the
    // head bone rotating on the face path, which tears the neck seam on its
    // own. See the `gestureDisableHeadEuler` JSDoc.
    gestureDisableHeadEuler: {
      'lean-forward': true,
      'lean-back': true,
      shrug: true,
    },
  },
  {
    id: 'f4-kiera',
    name: 'Kiera',
    url: '/models/f4-kiera.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-kiera-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/kiera-sm.jpg',
    portrait: '/portraits/kiera-lg.jpg',
    description: 'Female avatar - Kiera top, skirt, Vera hair',
    source: 'Xandra3D (F4 Modern Girls)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-GB-SoniaNeural',
    defaultAppearance: { eyeColor: '#8B7355', hairColor: '#7A3D2E' }, // hazel eyes, soft auburn hair
    // Jul 28 2026 ROLLBACK: lean + shrug RE-DISABLED (skeletal off, face-only).
    //
    // The Jun 13 2026 re-enable rested on a "corrected" min-gap probe that has
    // since been retracted — it measured a FIXED point, not the mesh (v2 probe
    // rewrite, commit 0490cc6). A clean bind-pose merge report is necessary but
    // NOT sufficient: it says the armatures are compatible, not that the
    // skinned result holds together at gesture peak.
    //
    // Kiera is SUSPECT-AND-UNVERIFIED rather than confirmed-failing: only a
    // partial capture was possible during the live QA pass, and in the one
    // usable shrug frame the hair mesh fragmented worse than at rest with a
    // hole opening at the left temple. Suspect-and-unverified does not stay
    // enabled on a production surface, so these go off with her siblings until
    // a full capture clears them.
    //
    // Re-enabling requires a fresh evaluation with the v2 probe PLUS a visual
    // pass at gesture peak. See the gesture-policy lock test in
    // `modelRegistry.gestures.test.ts` before changing these values.
    gestureOverrides: {
      'lean-forward': false,
      'lean-back': false,
      shrug: false,
    },
    // Required alongside the override above — the override alone leaves the
    // head bone rotating on the face path, which tears the neck seam on its
    // own. See the `gestureDisableHeadEuler` JSDoc.
    gestureDisableHeadEuler: {
      'lean-forward': true,
      'lean-back': true,
      shrug: true,
    },
  },
  {
    id: 'f4-naomi',
    name: 'Naomi',
    url: '/models/f4-naomi.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-naomi-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/naomi-sm.jpg',
    portrait: '/portraits/naomi-lg.jpg',
    description: 'Female avatar - Naomi top, pants, boots, bun hairstyle',
    source: 'Xandra3D (F4 Modern Girls)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-AU-CarlyNeural',
    defaultAppearance: { eyeColor: '#4A3728', hairColor: '#2A2018' }, // dark brown eyes, very dark brown hair
    // Jul 28 2026 ROLLBACK: lean + shrug RE-DISABLED (skeletal off, face-only).
    //
    // The Jun 13 2026 re-enable rested on a "corrected" min-gap probe that has
    // since been retracted — it measured a FIXED point, not the mesh (v2 probe
    // rewrite, commit 0490cc6). The clean 1e-3 merge report is necessary but
    // NOT sufficient: compatible armatures do not guarantee the skinned result
    // holds together at gesture peak. The "torso splits" reports it dismissed
    // were real.
    //
    // Visual QA against the live production runtime host (rev 62, bundle
    // main-DFwSNaxf.js) confirmed REAL tearing at gesture peak on BOTH lean and
    // shrug: the camisole hemline splits into layered shards, both straps
    // serrate, and a notch opens where the left strap meets the shoulder. Cara
    // (f4-xandra), never disabled, stayed clean under the identical test.
    //
    // Re-enabling requires a fresh evaluation with the v2 probe PLUS a visual
    // pass at gesture peak. See the gesture-policy lock test in
    // `modelRegistry.gestures.test.ts` before changing these values.
    gestureOverrides: {
      'lean-forward': false,
      'lean-back': false,
      shrug: false,
    },
    // Required alongside the override above — the override alone leaves the
    // head bone rotating on the face path, which tears the neck seam on its
    // own. See the `gestureDisableHeadEuler` JSDoc.
    gestureDisableHeadEuler: {
      'lean-forward': true,
      'lean-back': true,
      shrug: true,
    },
  },
  {
    id: 'f4-riley',
    name: 'Mia',
    url: '/models/f4-riley.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-riley-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/mia-sm.jpg',
    portrait: '/portraits/mia-lg.jpg',
    thumbnailZoom: 'scale(1.2) translateY(-8%)',
    description: 'Female avatar - dress, mid-length hair',
    source: 'Xandra3D (F4 Modern Girls)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-US-JennyNeural',
    defaultAppearance: { eyeColor: '#6B8E9B', hairColor: '#C4A67A' }, // blue-gray eyes, warm honey-blonde hair
    // Jul 28 2026 ROLLBACK: lean + shrug RE-DISABLED (skeletal off, face-only).
    //
    // The Jun 13 2026 re-enable rested on a "corrected" min-gap probe that has
    // since been retracted — it measured a FIXED point, not the mesh (v2 probe
    // rewrite, commit 0490cc6). "Same F4 family as Kiera/Naomi" was never
    // evidence of anything on its own.
    //
    // Visual QA against the live production runtime host (rev 62, bundle
    // main-DFwSNaxf.js) confirmed REAL tearing at gesture peak and this is the
    // WORST failure in the set: the turtleneck collar disintegrates into
    // floating fragments and skin punches through the sweater across the chest
    // and shoulder. Fails on both lean and shrug. Cara (f4-xandra), never
    // disabled, stayed clean under the identical test.
    //
    // Re-enabling requires a fresh evaluation with the v2 probe PLUS a visual
    // pass at gesture peak. See the gesture-policy lock test in
    // `modelRegistry.gestures.test.ts` before changing these values.
    gestureOverrides: {
      'lean-forward': false,
      'lean-back': false,
      shrug: false,
    },
    // Required alongside the override above — the override alone leaves the
    // head bone rotating on the face path, which tears the neck seam on its
    // own. See the `gestureDisableHeadEuler` JSDoc.
    gestureDisableHeadEuler: {
      'lean-forward': true,
      'lean-back': true,
      shrug: true,
    },
  },
  {
    id: 'f4-shay',
    name: 'Nicola',
    url: '/models/f4-shay.glb',
    skeletonType: 'ue5',
    lowresUrl: '/models/f4-shay-lowres.glb',
    textureMode: 'embedded',
    blendshapeFormat: 'arkit',
    expectedMorphTargets: 51,
    thumbnail: '/portraits/nicola-sm.jpg',
    portrait: '/portraits/nicola-lg.jpg',
    description: 'Female avatar - top, shorts, boots, bob cut hair',
    source: 'Xandra3D (F4 Modern Girls)',
    ready: true,
    scale: 0.01,
    voiceId: 'en-IE-EmilyNeural',
    defaultAppearance: { eyeColor: '#4A90D9', hairColor: '#4A3125' }, // blue eyes, warm dark brown hair
    // Jun 13 2026: lean RE-ENABLED (follows global defaults). The earlier
    // disable was the rotation-confounded probe; corrected min-gap probe is
    // the guardrail. shrug stays face-only ON PURPOSE here — this is an
    // embedded-outfit rig (jacket+shirt on one torso mesh) that does shrug via
    // the morph allowlist below rather than shoulder bones (kept from Slice 8).
    gestureOverrides: {
      shrug: false,
    },
    gestureShrugMorphAllowlist: [...EMBEDDED_OUTFIT_SHRUG_MORPH_ALLOWLIST],
    gestureShrugMorphBoost: 1.15,
    gestureDisableHeadEuler: {
      shrug: true,
    },
    gestureFaceIntensityByGesture: {
      shrug: 1.1,
    },
    // Aug 13 2026: concerned lower lip rides up / pinches. Mood-only
    // override; viseme mouth is OK. Do not copy Sam's omit list.
    moodOverrides: { ...CONCERNED_BROWS_NO_LIP_PINCH },
  },
];

const LOCAL_BACKGROUND_REGISTRY: AvatarBackgroundConfig[] = [
  {
    id: "service-lounge-v1",
    name: "Service Lounge",
    family: "service-lounge",
    variantIndex: 1,
    category: "service",
    description: "Warm customer-facing reception lounge for service guide experiences.",
    agentId: "cara",
    defaultForModelIds: ["f4-xandra"],
    url: "/backgrounds/service-lounge-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/service-lounge-v1-desktop.webp",
      tablet: "/backgrounds/service-lounge-v1-tablet.webp",
      mobile: "/backgrounds/service-lounge-v1-mobile.webp",
      thumbnail: "/backgrounds/service-lounge-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "service-lounge-v2",
    name: "Service Lounge",
    family: "service-lounge",
    variantIndex: 2,
    category: "service",
    description: "Warm customer-facing reception lounge for service guide experiences.",
    agentId: "cara",
    url: "/backgrounds/service-lounge-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/service-lounge-v2-desktop.webp",
      tablet: "/backgrounds/service-lounge-v2-tablet.webp",
      mobile: "/backgrounds/service-lounge-v2-mobile.webp",
      thumbnail: "/backgrounds/service-lounge-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "compliance-suite-v1",
    name: "Compliance Suite",
    family: "compliance-suite",
    variantIndex: 1,
    category: "compliance",
    description: "Cool compliance and verification workspace for data capture flows.",
    agentId: "sam",
    defaultForModelIds: ["sam"],
    url: "/backgrounds/compliance-suite-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/compliance-suite-v1-desktop.webp",
      tablet: "/backgrounds/compliance-suite-v1-tablet.webp",
      mobile: "/backgrounds/compliance-suite-v1-mobile.webp",
      thumbnail: "/backgrounds/compliance-suite-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "compliance-suite-v2",
    name: "Compliance Suite",
    family: "compliance-suite",
    variantIndex: 2,
    category: "compliance",
    description: "Cool compliance and verification workspace for data capture flows.",
    agentId: "sam",
    url: "/backgrounds/compliance-suite-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/compliance-suite-v2-desktop.webp",
      tablet: "/backgrounds/compliance-suite-v2-tablet.webp",
      mobile: "/backgrounds/compliance-suite-v2-mobile.webp",
      thumbnail: "/backgrounds/compliance-suite-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "architecture-room-v1",
    name: "Architecture Room",
    family: "architecture-room",
    variantIndex: 1,
    category: "technical",
    description: "Technical architecture review room for system demos and walkthroughs.",
    agentId: "joel",
    defaultForModelIds: ["default", "m4-joel"],
    url: "/backgrounds/architecture-room-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/architecture-room-v1-desktop.webp",
      tablet: "/backgrounds/architecture-room-v1-tablet.webp",
      mobile: "/backgrounds/architecture-room-v1-mobile.webp",
      thumbnail: "/backgrounds/architecture-room-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "architecture-room-v2",
    name: "Architecture Room",
    family: "architecture-room",
    variantIndex: 2,
    category: "technical",
    description: "Technical architecture review room for system demos and walkthroughs.",
    agentId: "joel",
    url: "/backgrounds/architecture-room-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/architecture-room-v2-desktop.webp",
      tablet: "/backgrounds/architecture-room-v2-tablet.webp",
      mobile: "/backgrounds/architecture-room-v2-mobile.webp",
      thumbnail: "/backgrounds/architecture-room-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "voice-studio-v1",
    name: "Voice Studio",
    family: "voice-studio",
    variantIndex: 1,
    category: "voice",
    description: "Modern voice operations studio for call-synchronised avatar journeys.",
    agentId: "harper",
    defaultForModelIds: ["gymgirl-04"],
    url: "/backgrounds/voice-studio-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/voice-studio-v1-desktop.webp",
      tablet: "/backgrounds/voice-studio-v1-tablet.webp",
      mobile: "/backgrounds/voice-studio-v1-mobile.webp",
      thumbnail: "/backgrounds/voice-studio-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "voice-studio-v2",
    name: "Voice Studio",
    family: "voice-studio",
    variantIndex: 2,
    category: "voice",
    description: "Modern voice operations studio for call-synchronised avatar journeys.",
    agentId: "harper",
    url: "/backgrounds/voice-studio-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/voice-studio-v2-desktop.webp",
      tablet: "/backgrounds/voice-studio-v2-tablet.webp",
      mobile: "/backgrounds/voice-studio-v2-mobile.webp",
      thumbnail: "/backgrounds/voice-studio-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "flow-studio-v1",
    name: "Flow Studio",
    family: "flow-studio",
    variantIndex: 1,
    category: "design",
    description: "Service flow design studio for explaining architecture and guided reviews.",
    agentId: "blake",
    defaultForModelIds: ["gymgirl-05"],
    url: "/backgrounds/flow-studio-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/flow-studio-v1-desktop.webp",
      tablet: "/backgrounds/flow-studio-v1-tablet.webp",
      mobile: "/backgrounds/flow-studio-v1-mobile.webp",
      thumbnail: "/backgrounds/flow-studio-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "flow-studio-v2",
    name: "Flow Studio",
    family: "flow-studio",
    variantIndex: 2,
    category: "design",
    description: "Service flow design studio for explaining architecture and guided reviews.",
    agentId: "blake",
    url: "/backgrounds/flow-studio-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/flow-studio-v2-desktop.webp",
      tablet: "/backgrounds/flow-studio-v2-tablet.webp",
      mobile: "/backgrounds/flow-studio-v2-mobile.webp",
      thumbnail: "/backgrounds/flow-studio-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "onboarding-room-v1",
    name: "Onboarding Room",
    family: "onboarding-room",
    variantIndex: 1,
    category: "onboarding",
    description: "Bright onboarding room for product walkthroughs and first-time guides.",
    agentId: "ryan",
    defaultForModelIds: ["m4-ryan"],
    url: "/backgrounds/onboarding-room-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/onboarding-room-v1-desktop.webp",
      tablet: "/backgrounds/onboarding-room-v1-tablet.webp",
      mobile: "/backgrounds/onboarding-room-v1-mobile.webp",
      thumbnail: "/backgrounds/onboarding-room-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "onboarding-room-v2",
    name: "Onboarding Room",
    family: "onboarding-room",
    variantIndex: 2,
    category: "onboarding",
    description: "Bright onboarding room for product walkthroughs and first-time guides.",
    agentId: "ryan",
    url: "/backgrounds/onboarding-room-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/onboarding-room-v2-desktop.webp",
      tablet: "/backgrounds/onboarding-room-v2-tablet.webp",
      mobile: "/backgrounds/onboarding-room-v2-mobile.webp",
      thumbnail: "/backgrounds/onboarding-room-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "brand-lobby-v1",
    name: "Brand Lobby",
    family: "brand-lobby",
    variantIndex: 1,
    category: "brand",
    description: "Polished brand lobby for public-facing and marketing walkthroughs.",
    agentId: "ashley",
    defaultForModelIds: ["f4-ashley"],
    url: "/backgrounds/brand-lobby-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/brand-lobby-v1-desktop.webp",
      tablet: "/backgrounds/brand-lobby-v1-tablet.webp",
      mobile: "/backgrounds/brand-lobby-v1-mobile.webp",
      thumbnail: "/backgrounds/brand-lobby-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "brand-lobby-v2",
    name: "Brand Lobby",
    family: "brand-lobby",
    variantIndex: 2,
    category: "brand",
    description: "Polished brand lobby for public-facing and marketing walkthroughs.",
    agentId: "ashley",
    url: "/backgrounds/brand-lobby-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/brand-lobby-v2-desktop.webp",
      tablet: "/backgrounds/brand-lobby-v2-tablet.webp",
      mobile: "/backgrounds/brand-lobby-v2-mobile.webp",
      thumbnail: "/backgrounds/brand-lobby-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "support-desk-v1",
    name: "Support Desk",
    family: "support-desk",
    variantIndex: 1,
    category: "support",
    description: "Calm support workspace for troubleshooting and escalation flows.",
    agentId: "jason",
    defaultForModelIds: ["m4-jason"],
    url: "/backgrounds/support-desk-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/support-desk-v1-desktop.webp",
      tablet: "/backgrounds/support-desk-v1-tablet.webp",
      mobile: "/backgrounds/support-desk-v1-mobile.webp",
      thumbnail: "/backgrounds/support-desk-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "support-desk-v2",
    name: "Support Desk",
    family: "support-desk",
    variantIndex: 2,
    category: "support",
    description: "Calm support workspace for troubleshooting and escalation flows.",
    agentId: "jason",
    url: "/backgrounds/support-desk-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/support-desk-v2-desktop.webp",
      tablet: "/backgrounds/support-desk-v2-tablet.webp",
      mobile: "/backgrounds/support-desk-v2-mobile.webp",
      thumbnail: "/backgrounds/support-desk-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "training-room-v1",
    name: "Training Room",
    family: "training-room",
    variantIndex: 1,
    category: "training",
    description: "Training and compliance learning room for staff onboarding sessions.",
    agentId: "kiera",
    defaultForModelIds: ["f4-kiera"],
    url: "/backgrounds/training-room-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/training-room-v1-desktop.webp",
      tablet: "/backgrounds/training-room-v1-tablet.webp",
      mobile: "/backgrounds/training-room-v1-mobile.webp",
      thumbnail: "/backgrounds/training-room-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "training-room-v2",
    name: "Training Room",
    family: "training-room",
    variantIndex: 2,
    category: "training",
    description: "Training and compliance learning room for staff onboarding sessions.",
    agentId: "kiera",
    url: "/backgrounds/training-room-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/training-room-v2-desktop.webp",
      tablet: "/backgrounds/training-room-v2-tablet.webp",
      mobile: "/backgrounds/training-room-v2-mobile.webp",
      thumbnail: "/backgrounds/training-room-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "operations-hub-v1",
    name: "Operations Hub",
    family: "operations-hub",
    variantIndex: 1,
    category: "operations",
    description: "Operations analytics room for process and data review experiences.",
    agentId: "naomi",
    defaultForModelIds: ["f4-naomi"],
    url: "/backgrounds/operations-hub-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/operations-hub-v1-desktop.webp",
      tablet: "/backgrounds/operations-hub-v1-tablet.webp",
      mobile: "/backgrounds/operations-hub-v1-mobile.webp",
      thumbnail: "/backgrounds/operations-hub-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "operations-hub-v2",
    name: "Operations Hub",
    family: "operations-hub",
    variantIndex: 2,
    category: "operations",
    description: "Operations analytics room for process and data review experiences.",
    agentId: "naomi",
    url: "/backgrounds/operations-hub-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/operations-hub-v2-desktop.webp",
      tablet: "/backgrounds/operations-hub-v2-tablet.webp",
      mobile: "/backgrounds/operations-hub-v2-mobile.webp",
      thumbnail: "/backgrounds/operations-hub-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "care-centre-v1",
    name: "Care Centre",
    family: "care-centre",
    variantIndex: 1,
    category: "care",
    description: "Warm public service centre for accessible citizen service journeys.",
    agentId: "mia",
    defaultForModelIds: ["gymgirl-03", "f4-riley"],
    url: "/backgrounds/care-centre-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/care-centre-v1-desktop.webp",
      tablet: "/backgrounds/care-centre-v1-tablet.webp",
      mobile: "/backgrounds/care-centre-v1-mobile.webp",
      thumbnail: "/backgrounds/care-centre-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "care-centre-v2",
    name: "Care Centre",
    family: "care-centre",
    variantIndex: 2,
    category: "care",
    description: "Warm public service centre for accessible citizen service journeys.",
    agentId: "mia",
    url: "/backgrounds/care-centre-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/care-centre-v2-desktop.webp",
      tablet: "/backgrounds/care-centre-v2-tablet.webp",
      mobile: "/backgrounds/care-centre-v2-mobile.webp",
      thumbnail: "/backgrounds/care-centre-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "concierge-lobby-v1",
    name: "Concierge Lobby",
    family: "concierge-lobby",
    variantIndex: 1,
    category: "concierge",
    description: "Crisp digital concierge lobby for fast guided service interactions.",
    agentId: "nicola",
    defaultForModelIds: ["f4-shay"],
    url: "/backgrounds/concierge-lobby-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/concierge-lobby-v1-desktop.webp",
      tablet: "/backgrounds/concierge-lobby-v1-tablet.webp",
      mobile: "/backgrounds/concierge-lobby-v1-mobile.webp",
      thumbnail: "/backgrounds/concierge-lobby-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "concierge-lobby-v2",
    name: "Concierge Lobby",
    family: "concierge-lobby",
    variantIndex: 2,
    category: "concierge",
    description: "Crisp digital concierge lobby for fast guided service interactions.",
    agentId: "nicola",
    url: "/backgrounds/concierge-lobby-v2-desktop.webp",
    variants: {
      desktop: "/backgrounds/concierge-lobby-v2-desktop.webp",
      tablet: "/backgrounds/concierge-lobby-v2-tablet.webp",
      mobile: "/backgrounds/concierge-lobby-v2-mobile.webp",
      thumbnail: "/backgrounds/concierge-lobby-v2-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "coffee-cafe-v1",
    name: "Coffee Cafe",
    family: "coffee-cafe",
    variantIndex: 1,
    category: "hospitality",
    description: "Warm independent coffee shop with brick wall and brewing equipment.",
    url: "/backgrounds/coffee-cafe-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/coffee-cafe-v1-desktop.webp",
      tablet: "/backgrounds/coffee-cafe-v1-tablet.webp",
      mobile: "/backgrounds/coffee-cafe-v1-mobile.webp",
      thumbnail: "/backgrounds/coffee-cafe-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "pizza-kitchen-v1",
    name: "Pizza Kitchen",
    family: "pizza-kitchen",
    variantIndex: 1,
    category: "hospitality",
    description: "Authentic Italian pizzeria with wood-fired oven and rustic tile floor.",
    url: "/backgrounds/pizza-kitchen-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/pizza-kitchen-v1-desktop.webp",
      tablet: "/backgrounds/pizza-kitchen-v1-tablet.webp",
      mobile: "/backgrounds/pizza-kitchen-v1-mobile.webp",
      thumbnail: "/backgrounds/pizza-kitchen-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "sushi-bar-v1",
    name: "Sushi Bar",
    family: "sushi-bar",
    variantIndex: 1,
    category: "hospitality",
    description: "Modern Japanese sushi bar with paper lanterns and dark wood interior.",
    url: "/backgrounds/sushi-bar-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/sushi-bar-v1-desktop.webp",
      tablet: "/backgrounds/sushi-bar-v1-tablet.webp",
      mobile: "/backgrounds/sushi-bar-v1-mobile.webp",
      thumbnail: "/backgrounds/sushi-bar-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "fine-dining-v1",
    name: "Fine Dining",
    family: "fine-dining",
    variantIndex: 1,
    category: "hospitality",
    description: "Upscale fine dining restaurant with elegant table settings and ambient lighting.",
    url: "/backgrounds/fine-dining-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/fine-dining-v1-desktop.webp",
      tablet: "/backgrounds/fine-dining-v1-tablet.webp",
      mobile: "/backgrounds/fine-dining-v1-mobile.webp",
      thumbnail: "/backgrounds/fine-dining-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "burger-diner-v1",
    name: "Burger Diner",
    family: "burger-diner",
    variantIndex: 1,
    category: "hospitality",
    description: "Retro American diner with red leather booths and checkerboard floor.",
    url: "/backgrounds/burger-diner-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/burger-diner-v1-desktop.webp",
      tablet: "/backgrounds/burger-diner-v1-tablet.webp",
      mobile: "/backgrounds/burger-diner-v1-mobile.webp",
      thumbnail: "/backgrounds/burger-diner-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "brewpub-hall-v1",
    name: "Brewpub Hall",
    family: "brewpub-hall",
    variantIndex: 1,
    category: "hospitality",
    description: "Industrial brewpub hall with copper tanks and exposed beams.",
    url: "/backgrounds/brewpub-hall-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/brewpub-hall-v1-desktop.webp",
      tablet: "/backgrounds/brewpub-hall-v1-tablet.webp",
      mobile: "/backgrounds/brewpub-hall-v1-mobile.webp",
      thumbnail: "/backgrounds/brewpub-hall-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "food-court-v1",
    name: "Food Court",
    family: "food-court",
    variantIndex: 1,
    category: "hospitality",
    description: "Bright contemporary food court with mixed seating and overhead signage.",
    url: "/backgrounds/food-court-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/food-court-v1-desktop.webp",
      tablet: "/backgrounds/food-court-v1-tablet.webp",
      mobile: "/backgrounds/food-court-v1-mobile.webp",
      thumbnail: "/backgrounds/food-court-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "bakery-counter-v1",
    name: "Bakery Counter",
    family: "bakery-counter",
    variantIndex: 1,
    category: "hospitality",
    description: "Artisan bakery with rustic shelves of fresh bread and pastries.",
    url: "/backgrounds/bakery-counter-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/bakery-counter-v1-desktop.webp",
      tablet: "/backgrounds/bakery-counter-v1-tablet.webp",
      mobile: "/backgrounds/bakery-counter-v1-mobile.webp",
      thumbnail: "/backgrounds/bakery-counter-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "wine-cellar-v1",
    name: "Wine Cellar",
    family: "wine-cellar",
    variantIndex: 1,
    category: "hospitality",
    description: "Atmospheric wine cellar with stone walls and oak barrels.",
    url: "/backgrounds/wine-cellar-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/wine-cellar-v1-desktop.webp",
      tablet: "/backgrounds/wine-cellar-v1-tablet.webp",
      mobile: "/backgrounds/wine-cellar-v1-mobile.webp",
      thumbnail: "/backgrounds/wine-cellar-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "hotel-lobby-v1",
    name: "Hotel Lobby",
    family: "hotel-lobby",
    variantIndex: 1,
    category: "hospitality",
    description: "Refined hotel lobby with marble floor and warm pendant lighting.",
    url: "/backgrounds/hotel-lobby-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/hotel-lobby-v1-desktop.webp",
      tablet: "/backgrounds/hotel-lobby-v1-tablet.webp",
      mobile: "/backgrounds/hotel-lobby-v1-mobile.webp",
      thumbnail: "/backgrounds/hotel-lobby-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "resort-pool-v1",
    name: "Resort Pool",
    family: "resort-pool",
    variantIndex: 1,
    category: "hospitality",
    description: "Luxury resort poolside terrace with palms and sun loungers.",
    url: "/backgrounds/resort-pool-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/resort-pool-v1-desktop.webp",
      tablet: "/backgrounds/resort-pool-v1-tablet.webp",
      mobile: "/backgrounds/resort-pool-v1-mobile.webp",
      thumbnail: "/backgrounds/resort-pool-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "boutique-hotel-v1",
    name: "Boutique Hotel",
    family: "boutique-hotel",
    variantIndex: 1,
    category: "hospitality",
    description: "Boutique hotel reception with curated art and warm timber finishes.",
    url: "/backgrounds/boutique-hotel-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/boutique-hotel-v1-desktop.webp",
      tablet: "/backgrounds/boutique-hotel-v1-tablet.webp",
      mobile: "/backgrounds/boutique-hotel-v1-mobile.webp",
      thumbnail: "/backgrounds/boutique-hotel-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "fashion-boutique-v1",
    name: "Fashion Boutique",
    family: "fashion-boutique",
    variantIndex: 1,
    category: "retail",
    description: "High-end fashion boutique with curated displays and polished floors.",
    url: "/backgrounds/fashion-boutique-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/fashion-boutique-v1-desktop.webp",
      tablet: "/backgrounds/fashion-boutique-v1-tablet.webp",
      mobile: "/backgrounds/fashion-boutique-v1-mobile.webp",
      thumbnail: "/backgrounds/fashion-boutique-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "jewellery-showroom-v1",
    name: "Jewellery Showroom",
    family: "jewellery-showroom",
    variantIndex: 1,
    category: "retail",
    description: "Luxury jewellery showroom with backlit cases and dark accent walls.",
    url: "/backgrounds/jewellery-showroom-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/jewellery-showroom-v1-desktop.webp",
      tablet: "/backgrounds/jewellery-showroom-v1-tablet.webp",
      mobile: "/backgrounds/jewellery-showroom-v1-mobile.webp",
      thumbnail: "/backgrounds/jewellery-showroom-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "electronics-store-v1",
    name: "Electronics Store",
    family: "electronics-store",
    variantIndex: 1,
    category: "retail",
    description: "Modern electronics retail floor with bright LED-lit shelving.",
    url: "/backgrounds/electronics-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/electronics-store-v1-desktop.webp",
      tablet: "/backgrounds/electronics-store-v1-tablet.webp",
      mobile: "/backgrounds/electronics-store-v1-mobile.webp",
      thumbnail: "/backgrounds/electronics-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "sports-shop-v1",
    name: "Sports Shop",
    family: "sports-shop",
    variantIndex: 1,
    category: "retail",
    description: "Energetic sports retail floor with apparel and equipment displays.",
    url: "/backgrounds/sports-shop-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/sports-shop-v1-desktop.webp",
      tablet: "/backgrounds/sports-shop-v1-tablet.webp",
      mobile: "/backgrounds/sports-shop-v1-mobile.webp",
      thumbnail: "/backgrounds/sports-shop-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "gift-shop-v1",
    name: "Gift Shop",
    family: "gift-shop",
    variantIndex: 1,
    category: "retail",
    description: "Cosy gift shop with eclectic homewares on warm wood shelving.",
    url: "/backgrounds/gift-shop-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/gift-shop-v1-desktop.webp",
      tablet: "/backgrounds/gift-shop-v1-tablet.webp",
      mobile: "/backgrounds/gift-shop-v1-mobile.webp",
      thumbnail: "/backgrounds/gift-shop-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "hardware-store-v1",
    name: "Hardware Store",
    family: "hardware-store",
    variantIndex: 1,
    category: "retail",
    description: "Practical hardware store aisle with tools and supplies.",
    url: "/backgrounds/hardware-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/hardware-store-v1-desktop.webp",
      tablet: "/backgrounds/hardware-store-v1-tablet.webp",
      mobile: "/backgrounds/hardware-store-v1-mobile.webp",
      thumbnail: "/backgrounds/hardware-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "bookstore-aisles-v1",
    name: "Bookstore Aisles",
    family: "bookstore-aisles",
    variantIndex: 1,
    category: "retail",
    description: "Independent bookshop with floor-to-ceiling timber shelves.",
    url: "/backgrounds/bookstore-aisles-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/bookstore-aisles-v1-desktop.webp",
      tablet: "/backgrounds/bookstore-aisles-v1-tablet.webp",
      mobile: "/backgrounds/bookstore-aisles-v1-mobile.webp",
      thumbnail: "/backgrounds/bookstore-aisles-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "supermarket-aisles-v1",
    name: "Supermarket Aisles",
    family: "supermarket-aisles",
    variantIndex: 1,
    category: "retail",
    description: "Clean supermarket aisle with stocked shelves under bright fluorescent lighting.",
    url: "/backgrounds/supermarket-aisles-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/supermarket-aisles-v1-desktop.webp",
      tablet: "/backgrounds/supermarket-aisles-v1-tablet.webp",
      mobile: "/backgrounds/supermarket-aisles-v1-mobile.webp",
      thumbnail: "/backgrounds/supermarket-aisles-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "shoe-store-v1",
    name: "Shoe Store",
    family: "shoe-store",
    variantIndex: 1,
    category: "retail",
    description: "Modern shoe boutique with neat rows of footwear on backlit displays.",
    url: "/backgrounds/shoe-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/shoe-store-v1-desktop.webp",
      tablet: "/backgrounds/shoe-store-v1-tablet.webp",
      mobile: "/backgrounds/shoe-store-v1-mobile.webp",
      thumbnail: "/backgrounds/shoe-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "mobile-store-v1",
    name: "Mobile Store",
    family: "mobile-store",
    variantIndex: 1,
    category: "retail",
    description: "Bright mobile phone retail floor with display tables and signage.",
    url: "/backgrounds/mobile-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/mobile-store-v1-desktop.webp",
      tablet: "/backgrounds/mobile-store-v1-tablet.webp",
      mobile: "/backgrounds/mobile-store-v1-mobile.webp",
      thumbnail: "/backgrounds/mobile-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "toy-store-v1",
    name: "Toy Store",
    family: "toy-store",
    variantIndex: 1,
    category: "retail",
    description: "Colourful toy store with shelves of products and playful overhead lighting.",
    url: "/backgrounds/toy-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/toy-store-v1-desktop.webp",
      tablet: "/backgrounds/toy-store-v1-tablet.webp",
      mobile: "/backgrounds/toy-store-v1-mobile.webp",
      thumbnail: "/backgrounds/toy-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "car-showroom-v1",
    name: "Car Showroom",
    family: "car-showroom",
    variantIndex: 1,
    category: "retail",
    description: "Premium automotive showroom with polished floor and feature lighting.",
    url: "/backgrounds/car-showroom-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/car-showroom-v1-desktop.webp",
      tablet: "/backgrounds/car-showroom-v1-tablet.webp",
      mobile: "/backgrounds/car-showroom-v1-mobile.webp",
      thumbnail: "/backgrounds/car-showroom-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "telco-store-v1",
    name: "Telco Store",
    family: "telco-store",
    variantIndex: 1,
    category: "services",
    description: "Modern telecommunications retail store with device displays and branded walls.",
    url: "/backgrounds/telco-store-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/telco-store-v1-desktop.webp",
      tablet: "/backgrounds/telco-store-v1-tablet.webp",
      mobile: "/backgrounds/telco-store-v1-mobile.webp",
      thumbnail: "/backgrounds/telco-store-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "accounting-office-v1",
    name: "Accounting Office",
    family: "accounting-office",
    variantIndex: 1,
    category: "services",
    description: "Calm accounting office with neutral palette and minimal furniture.",
    url: "/backgrounds/accounting-office-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/accounting-office-v1-desktop.webp",
      tablet: "/backgrounds/accounting-office-v1-tablet.webp",
      mobile: "/backgrounds/accounting-office-v1-mobile.webp",
      thumbnail: "/backgrounds/accounting-office-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "legal-chambers-v1",
    name: "Legal Chambers",
    family: "legal-chambers",
    variantIndex: 1,
    category: "services",
    description: "Traditional legal chambers with bookcases and polished timber.",
    url: "/backgrounds/legal-chambers-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/legal-chambers-v1-desktop.webp",
      tablet: "/backgrounds/legal-chambers-v1-tablet.webp",
      mobile: "/backgrounds/legal-chambers-v1-mobile.webp",
      thumbnail: "/backgrounds/legal-chambers-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "marketing-agency-v1",
    name: "Marketing Agency",
    family: "marketing-agency",
    variantIndex: 1,
    category: "services",
    description: "Open-plan marketing agency with creative workspaces and brand colour.",
    url: "/backgrounds/marketing-agency-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/marketing-agency-v1-desktop.webp",
      tablet: "/backgrounds/marketing-agency-v1-tablet.webp",
      mobile: "/backgrounds/marketing-agency-v1-mobile.webp",
      thumbnail: "/backgrounds/marketing-agency-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "creative-agency-v1",
    name: "Creative Agency",
    family: "creative-agency",
    variantIndex: 1,
    category: "services",
    description: "Loft-style creative agency with exposed brick and polished concrete floor.",
    url: "/backgrounds/creative-agency-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/creative-agency-v1-desktop.webp",
      tablet: "/backgrounds/creative-agency-v1-tablet.webp",
      mobile: "/backgrounds/creative-agency-v1-mobile.webp",
      thumbnail: "/backgrounds/creative-agency-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "tech-startup-v1",
    name: "Tech Startup",
    family: "tech-startup",
    variantIndex: 1,
    category: "services",
    description: "Bright tech startup office with open plan layout and large windows.",
    url: "/backgrounds/tech-startup-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/tech-startup-v1-desktop.webp",
      tablet: "/backgrounds/tech-startup-v1-tablet.webp",
      mobile: "/backgrounds/tech-startup-v1-mobile.webp",
      thumbnail: "/backgrounds/tech-startup-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "coworking-space-v1",
    name: "Coworking Space",
    family: "coworking-space",
    variantIndex: 1,
    category: "services",
    description: "Flexible coworking lounge with mixed seating and biophilic accents.",
    url: "/backgrounds/coworking-space-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/coworking-space-v1-desktop.webp",
      tablet: "/backgrounds/coworking-space-v1-tablet.webp",
      mobile: "/backgrounds/coworking-space-v1-mobile.webp",
      thumbnail: "/backgrounds/coworking-space-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "realestate-office-v1",
    name: "Real Estate Office",
    family: "realestate-office",
    variantIndex: 1,
    category: "services",
    description: "Polished real estate office with glass-walled meeting rooms.",
    url: "/backgrounds/realestate-office-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/realestate-office-v1-desktop.webp",
      tablet: "/backgrounds/realestate-office-v1-tablet.webp",
      mobile: "/backgrounds/realestate-office-v1-mobile.webp",
      thumbnail: "/backgrounds/realestate-office-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "insurance-office-v1",
    name: "Insurance Office",
    family: "insurance-office",
    variantIndex: 1,
    category: "services",
    description: "Professional insurance branch with neutral tones and wayfinding.",
    url: "/backgrounds/insurance-office-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/insurance-office-v1-desktop.webp",
      tablet: "/backgrounds/insurance-office-v1-tablet.webp",
      mobile: "/backgrounds/insurance-office-v1-mobile.webp",
      thumbnail: "/backgrounds/insurance-office-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "bank-branch-v1",
    name: "Bank Branch",
    family: "bank-branch",
    variantIndex: 1,
    category: "services",
    description: "Modern retail bank branch with curved feature wall and bright lighting.",
    url: "/backgrounds/bank-branch-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/bank-branch-v1-desktop.webp",
      tablet: "/backgrounds/bank-branch-v1-tablet.webp",
      mobile: "/backgrounds/bank-branch-v1-mobile.webp",
      thumbnail: "/backgrounds/bank-branch-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "travel-agency-v1",
    name: "Travel Agency",
    family: "travel-agency",
    variantIndex: 1,
    category: "services",
    description: "Inviting travel agency lobby with destination imagery on feature walls.",
    url: "/backgrounds/travel-agency-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/travel-agency-v1-desktop.webp",
      tablet: "/backgrounds/travel-agency-v1-tablet.webp",
      mobile: "/backgrounds/travel-agency-v1-mobile.webp",
      thumbnail: "/backgrounds/travel-agency-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "government-office-v1",
    name: "Government Office",
    family: "government-office",
    variantIndex: 1,
    category: "services",
    description: "Civic government office reception with formal seating and signage.",
    url: "/backgrounds/government-office-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/government-office-v1-desktop.webp",
      tablet: "/backgrounds/government-office-v1-tablet.webp",
      mobile: "/backgrounds/government-office-v1-mobile.webp",
      thumbnail: "/backgrounds/government-office-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "medical-clinic-v1",
    name: "Medical Clinic",
    family: "medical-clinic",
    variantIndex: 1,
    category: "health",
    description: "Bright medical clinic reception with calming neutral palette.",
    url: "/backgrounds/medical-clinic-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/medical-clinic-v1-desktop.webp",
      tablet: "/backgrounds/medical-clinic-v1-tablet.webp",
      mobile: "/backgrounds/medical-clinic-v1-mobile.webp",
      thumbnail: "/backgrounds/medical-clinic-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "dental-surgery-v1",
    name: "Dental Surgery",
    family: "dental-surgery",
    variantIndex: 1,
    category: "health",
    description: "Modern dental surgery reception with clean lines and soft lighting.",
    url: "/backgrounds/dental-surgery-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/dental-surgery-v1-desktop.webp",
      tablet: "/backgrounds/dental-surgery-v1-tablet.webp",
      mobile: "/backgrounds/dental-surgery-v1-mobile.webp",
      thumbnail: "/backgrounds/dental-surgery-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "vet-clinic-v1",
    name: "Vet Clinic",
    family: "vet-clinic",
    variantIndex: 1,
    category: "health",
    description: "Friendly veterinary clinic waiting area with playful accents.",
    url: "/backgrounds/vet-clinic-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/vet-clinic-v1-desktop.webp",
      tablet: "/backgrounds/vet-clinic-v1-tablet.webp",
      mobile: "/backgrounds/vet-clinic-v1-mobile.webp",
      thumbnail: "/backgrounds/vet-clinic-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "day-spa-v1",
    name: "Day Spa",
    family: "day-spa",
    variantIndex: 1,
    category: "health",
    description: "Tranquil day spa interior with stone wall and ambient light.",
    url: "/backgrounds/day-spa-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/day-spa-v1-desktop.webp",
      tablet: "/backgrounds/day-spa-v1-tablet.webp",
      mobile: "/backgrounds/day-spa-v1-mobile.webp",
      thumbnail: "/backgrounds/day-spa-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "pharmacy-floor-v1",
    name: "Pharmacy Floor",
    family: "pharmacy-floor",
    variantIndex: 1,
    category: "health",
    description: "Modern pharmacy retail floor with clear product zones and bright lighting.",
    url: "/backgrounds/pharmacy-floor-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/pharmacy-floor-v1-desktop.webp",
      tablet: "/backgrounds/pharmacy-floor-v1-tablet.webp",
      mobile: "/backgrounds/pharmacy-floor-v1-mobile.webp",
      thumbnail: "/backgrounds/pharmacy-floor-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "school-classroom-v1",
    name: "School Classroom",
    family: "school-classroom",
    variantIndex: 1,
    category: "education",
    description: "Bright school classroom with engaging learning displays and natural light.",
    url: "/backgrounds/school-classroom-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/school-classroom-v1-desktop.webp",
      tablet: "/backgrounds/school-classroom-v1-tablet.webp",
      mobile: "/backgrounds/school-classroom-v1-mobile.webp",
      thumbnail: "/backgrounds/school-classroom-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "university-library-v1",
    name: "University Library",
    family: "university-library",
    variantIndex: 1,
    category: "education",
    description: "Grand university library with high ceilings and rows of bookshelves.",
    url: "/backgrounds/university-library-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/university-library-v1-desktop.webp",
      tablet: "/backgrounds/university-library-v1-tablet.webp",
      mobile: "/backgrounds/university-library-v1-mobile.webp",
      thumbnail: "/backgrounds/university-library-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "public-library-v1",
    name: "Public Library",
    family: "public-library",
    variantIndex: 1,
    category: "education",
    description: "Friendly public library with reading nooks and timber furniture.",
    url: "/backgrounds/public-library-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/public-library-v1-desktop.webp",
      tablet: "/backgrounds/public-library-v1-tablet.webp",
      mobile: "/backgrounds/public-library-v1-mobile.webp",
      thumbnail: "/backgrounds/public-library-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "science-lab-v1",
    name: "Science Lab",
    family: "science-lab",
    variantIndex: 1,
    category: "education",
    description: "Modern science laboratory with benches, glassware and instrumentation.",
    url: "/backgrounds/science-lab-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/science-lab-v1-desktop.webp",
      tablet: "/backgrounds/science-lab-v1-tablet.webp",
      mobile: "/backgrounds/science-lab-v1-mobile.webp",
      thumbnail: "/backgrounds/science-lab-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "music-room-v1",
    name: "Music Room",
    family: "music-room",
    variantIndex: 1,
    category: "education",
    description: "Acoustic-treated music room with instruments and warm wood floor.",
    url: "/backgrounds/music-room-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/music-room-v1-desktop.webp",
      tablet: "/backgrounds/music-room-v1-tablet.webp",
      mobile: "/backgrounds/music-room-v1-mobile.webp",
      thumbnail: "/backgrounds/music-room-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "yoga-studio-v1",
    name: "Yoga Studio",
    family: "yoga-studio",
    variantIndex: 1,
    category: "wellness",
    description: "Serene yoga studio with timber floor and natural light from large windows.",
    url: "/backgrounds/yoga-studio-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/yoga-studio-v1-desktop.webp",
      tablet: "/backgrounds/yoga-studio-v1-tablet.webp",
      mobile: "/backgrounds/yoga-studio-v1-mobile.webp",
      thumbnail: "/backgrounds/yoga-studio-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "gym-floor-v1",
    name: "Gym Floor",
    family: "gym-floor",
    variantIndex: 1,
    category: "wellness",
    description: "Functional gym training floor with rubber matting and conditioning equipment.",
    url: "/backgrounds/gym-floor-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/gym-floor-v1-desktop.webp",
      tablet: "/backgrounds/gym-floor-v1-tablet.webp",
      mobile: "/backgrounds/gym-floor-v1-mobile.webp",
      thumbnail: "/backgrounds/gym-floor-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "auto-workshop-v1",
    name: "Auto Workshop",
    family: "auto-workshop",
    variantIndex: 1,
    category: "industrial",
    description: "Professional auto workshop bay with hoists and tool walls.",
    url: "/backgrounds/auto-workshop-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/auto-workshop-v1-desktop.webp",
      tablet: "/backgrounds/auto-workshop-v1-tablet.webp",
      mobile: "/backgrounds/auto-workshop-v1-mobile.webp",
      thumbnail: "/backgrounds/auto-workshop-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "warehouse-floor-v1",
    name: "Warehouse Floor",
    family: "warehouse-floor",
    variantIndex: 1,
    category: "industrial",
    description: "Large warehouse floor with high racking and polished concrete.",
    url: "/backgrounds/warehouse-floor-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/warehouse-floor-v1-desktop.webp",
      tablet: "/backgrounds/warehouse-floor-v1-tablet.webp",
      mobile: "/backgrounds/warehouse-floor-v1-mobile.webp",
      thumbnail: "/backgrounds/warehouse-floor-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "factory-floor-v1",
    name: "Factory Floor",
    family: "factory-floor",
    variantIndex: 1,
    category: "industrial",
    description: "Modern factory floor with assembly lines and overhead lighting.",
    url: "/backgrounds/factory-floor-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/factory-floor-v1-desktop.webp",
      tablet: "/backgrounds/factory-floor-v1-tablet.webp",
      mobile: "/backgrounds/factory-floor-v1-mobile.webp",
      thumbnail: "/backgrounds/factory-floor-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "airport-lounge-v1",
    name: "Airport Lounge",
    family: "airport-lounge",
    variantIndex: 1,
    category: "travel",
    description: "Premium airport lounge with comfortable seating and feature wall.",
    url: "/backgrounds/airport-lounge-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/airport-lounge-v1-desktop.webp",
      tablet: "/backgrounds/airport-lounge-v1-tablet.webp",
      mobile: "/backgrounds/airport-lounge-v1-mobile.webp",
      thumbnail: "/backgrounds/airport-lounge-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "city-council-v1",
    name: "City Council",
    family: "city-council",
    variantIndex: 1,
    category: "civic",
    description: "City council chamber with stepped seating and timber panelling.",
    url: "/backgrounds/city-council-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/city-council-v1-desktop.webp",
      tablet: "/backgrounds/city-council-v1-tablet.webp",
      mobile: "/backgrounds/city-council-v1-mobile.webp",
      thumbnail: "/backgrounds/city-council-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "community-centre-v1",
    name: "Community Centre",
    family: "community-centre",
    variantIndex: 1,
    category: "civic",
    description: "Welcoming community centre hall with flexible seating and bright lighting.",
    url: "/backgrounds/community-centre-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/community-centre-v1-desktop.webp",
      tablet: "/backgrounds/community-centre-v1-tablet.webp",
      mobile: "/backgrounds/community-centre-v1-mobile.webp",
      thumbnail: "/backgrounds/community-centre-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
  {
    id: "green-screen-v1",
    name: "Green Screen",
    family: "green-screen",
    variantIndex: 1,
    category: "utility",
    description: "Solid chroma-key green background for keying the avatar into custom backgrounds.",
    url: "/backgrounds/green-screen-v1-desktop.webp",
    variants: {
      desktop: "/backgrounds/green-screen-v1-desktop.webp",
      tablet: "/backgrounds/green-screen-v1-tablet.webp",
      mobile: "/backgrounds/green-screen-v1-mobile.webp",
      thumbnail: "/backgrounds/green-screen-v1-thumb.webp",
    },
    width: 1920,
    height: 1080,
    focalPoint: { x: 0.5, y: 0.48 },
    ready: true,
  },
];

// ---------------------------------------------------------------------------
// Merged Registry (local + remote)
// ---------------------------------------------------------------------------

/** The active model registry. Starts with local, augmented by remote manifest. */
export let MODEL_REGISTRY: ModelConfig[] = [...LOCAL_REGISTRY];

/** The active background registry. Starts with local, augmented by remote manifest. */
export let BACKGROUND_REGISTRY: AvatarBackgroundConfig[] = [...LOCAL_BACKGROUND_REGISTRY];

/** Whether the remote manifest has been loaded */
let _remoteLoaded = false;

/** Callbacks waiting for remote registry load */
let _onLoadCallbacks: Array<() => void> = [];

/**
 * Load the remote model manifest from the avatar-library service.
 * Merges remote models into the registry (remote takes precedence for same ID).
 * Safe to call multiple times -- only fetches once.
 */
export async function loadRemoteRegistry(): Promise<ModelConfig[]> {
  if (_remoteLoaded) return MODEL_REGISTRY;

  const baseUrl = getModelLibraryBaseUrl();
  if (!baseUrl) {
    console.log('[ModelRegistry] No model library URL -- using local registry only');
    _remoteLoaded = true;
    return MODEL_REGISTRY;
  }

  try {
    const manifestUrl = `${baseUrl}/manifest.json`;
    console.log('[ModelRegistry] Loading remote manifest:', manifestUrl);

    const response = await fetch(manifestUrl, { cache: 'default' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const manifest: ModelManifest = await response.json();
    console.log(`[ModelRegistry] Loaded ${manifest.models.length} models from manifest v${manifest.version}`);

    // Merge: remote models override local ones with same ID; new ones are appended
    const merged = new Map<string, ModelConfig>();

    // Start with local
    for (const model of LOCAL_REGISTRY) {
      merged.set(model.id, model);
    }

    // Overlay remote with deep-merge to preserve local-only fields
    for (const remoteModel of manifest.models) {
      if (remoteModel.id === 'default') continue;
      const localModel = merged.get(remoteModel.id);
      if (localModel) {
        merged.set(remoteModel.id, {
          ...localModel,
          ...remoteModel,
          defaultAppearance: {
            ...localModel.defaultAppearance,
            ...remoteModel.defaultAppearance,
          },
        });
      } else {
        merged.set(remoteModel.id, remoteModel);
      }
    }

    MODEL_REGISTRY = Array.from(merged.values());

    if (Array.isArray(manifest.backgrounds)) {
      const mergedBackgrounds = new Map<string, AvatarBackgroundConfig>();
      for (const background of LOCAL_BACKGROUND_REGISTRY) {
        mergedBackgrounds.set(background.id, background);
      }
      for (const remoteBackground of manifest.backgrounds) {
        const localBackground = mergedBackgrounds.get(remoteBackground.id);
        mergedBackgrounds.set(remoteBackground.id, {
          ...localBackground,
          ...remoteBackground,
          variants: {
            ...localBackground?.variants,
            ...remoteBackground.variants,
          },
          focalPoint: {
            ...(localBackground?.focalPoint ?? {}),
            ...(remoteBackground.focalPoint ?? {}),
          },
        });
      }
      BACKGROUND_REGISTRY = Array.from(mergedBackgrounds.values());
      console.log(`[ModelRegistry] Loaded ${manifest.backgrounds.length} backgrounds from manifest v${manifest.version}`);
    }

    _remoteLoaded = true;

    // Notify waiting callbacks
    for (const cb of _onLoadCallbacks) cb();
    _onLoadCallbacks = [];

    console.log(`[ModelRegistry] Registry now has ${MODEL_REGISTRY.length} models`);
    return MODEL_REGISTRY;
  } catch (err) {
    console.warn('[ModelRegistry] Failed to load remote manifest, using local registry:', err);
    _remoteLoaded = true;
    return MODEL_REGISTRY;
  }
}

/**
 * Register a callback for when the remote registry finishes loading.
 * If already loaded, calls immediately.
 */
export function onRegistryLoaded(callback: () => void): void {
  if (_remoteLoaded) {
    callback();
  } else {
    _onLoadCallbacks.push(callback);
  }
}

/** Check if the remote manifest has been loaded */
export function isRegistryLoaded(): boolean {
  return _remoteLoaded;
}

// ---------------------------------------------------------------------------
// Registry API
// ---------------------------------------------------------------------------

/**
 * Resolve thumbnail/portrait relative paths to absolute library URLs.
 *
 * Also appends the content-hash `?v=` cache buster (Aug 29 2026): portrait
 * art shares the GLBs' serving reality — fixed URL, year-long immutable
 * cache on models.camlinconnect.com.au — so replaced art kept rendering as
 * the OLD profile image on clients that had cached it (the Decision Memory
 * "wrong profile image" defect). Byte-derived busters per
 * .cursor/rules/ios-cache-busting.mdc; unknown paths pass through unchanged.
 */
function resolveModelAssets(model: ModelConfig): ModelConfig {
  const base = getModelLibraryBaseUrl();

  const resolved = { ...model };
  if (resolved.thumbnail) {
    resolved.thumbnail = base && !resolved.thumbnail.startsWith('http')
      ? appendModelAssetVersion(joinAssetUrl(base, resolved.thumbnail))
      : appendModelAssetVersion(resolved.thumbnail);
  }
  if (resolved.portrait) {
    resolved.portrait = base && !resolved.portrait.startsWith('http')
      ? appendModelAssetVersion(joinAssetUrl(base, resolved.portrait))
      : appendModelAssetVersion(resolved.portrait);
  }
  return resolved;
}

export function resolveBackgroundAssetUrl(url: string): string {
  if (url.startsWith('/backgrounds/')) {
    return resolveSharedModelLibraryAssetUrl(url);
  }
  return resolveAvatarRuntimeAssetUrl(url);
}

function resolveBackgroundAssets(background: AvatarBackgroundConfig): AvatarBackgroundConfig {
  const resolved: AvatarBackgroundConfig = {
    ...background,
    url: resolveBackgroundAssetUrl(background.url),
  };

  if (background.variants) {
    resolved.variants = Object.fromEntries(
      Object.entries(background.variants).map(([key, value]) => [
        key,
        value ? resolveBackgroundAssetUrl(value) : value,
      ])
    ) as Partial<Record<AvatarBackgroundVariant, string>>;
  }

  return resolved;
}

/** Get all production-ready models (ready !== false) */
export function getAllModels(): ModelConfig[] {
  return MODEL_REGISTRY
    .filter(m => m.ready !== false)
    .map(resolveModelAssets)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Get all registered models including broken/not-ready ones */
export function getAllModelsIncludingNotReady(): ModelConfig[] {
  return MODEL_REGISTRY.map(resolveModelAssets);
}

/** Get a model by ID */
export function getModelById(id: string): ModelConfig | undefined {
  const model = MODEL_REGISTRY.find(m => m.id === id);
  return model ? resolveModelAssets(model) : undefined;
}

/** Get a model by its URL (for reverse-lookup from Avatar.tsx which only knows the URL) */
function incomingUrlMatches(registryUrl: string | undefined, incoming: string): boolean {
  if (!registryUrl) return false;
  if (registryUrl === incoming) return true;
  if (resolveModelUrl(registryUrl) === incoming) return true;
  const registryFile = registryUrl.split('/').pop();
  const incomingFile = incoming.split('/').pop()?.split('?')[0];
  return !!registryFile && registryFile === incomingFile;
}

export function getModelByUrl(url: string): ModelConfig | undefined {
  const model = MODEL_REGISTRY.find(m => {
    if (incomingUrlMatches(m.url, url)) return true;
    if (incomingUrlMatches(m.lowresUrl, url)) return true;
    return (m.urlAliases ?? []).some((alias) => incomingUrlMatches(alias, url));
  });
  return model ? resolveModelAssets(model) : undefined;
}

/** Get models that have embedded textures (easiest to use) */
export function getEmbeddedModels(): ModelConfig[] {
  return MODEL_REGISTRY.filter(m => m.textureMode === 'embedded');
}

/** Get the model assigned to a specific agent */
export function getModelForAgent(agentId: string): ModelConfig | undefined {
  return MODEL_REGISTRY.find(m => m.agentId === agentId);
}

/** Check if a model uses embedded textures */
export function isEmbeddedTextureModel(modelId: string): boolean {
  const model = getModelById(modelId);
  return model?.textureMode === 'embedded';
}

/** Check if a model uses the Shenya hybrid workaround */
export function isHybridModel(modelId: string): boolean {
  const model = getModelById(modelId);
  return model?.textureMode === 'hybrid';
}

/** Get all production-ready generated backgrounds (ready !== false). */
export function getAllBackgrounds(): AvatarBackgroundConfig[] {
  return BACKGROUND_REGISTRY
    .filter(background => background.ready !== false)
    .map(resolveBackgroundAssets)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Get a generated background by ID. */
export function getBackgroundById(id: string): AvatarBackgroundConfig | undefined {
  const background = BACKGROUND_REGISTRY.find(candidate => candidate.id === id);
  return background ? resolveBackgroundAssets(background) : undefined;
}

/**
 * Group backgrounds by family slug. Each family is sorted by `variantIndex`
 * ascending. Stand-alone backgrounds without a `family` are returned as
 * single-item families keyed by their id.
 */
export function getBackgroundFamilies(): Array<{
  family: string;
  name: string;
  variants: AvatarBackgroundConfig[];
}> {
  const families = new Map<string, AvatarBackgroundConfig[]>();
  for (const bg of BACKGROUND_REGISTRY) {
    if (bg.ready === false) continue;
    const key = bg.family ?? bg.id;
    const list = families.get(key) ?? [];
    list.push(resolveBackgroundAssets(bg));
    families.set(key, list);
  }
  const out: Array<{ family: string; name: string; variants: AvatarBackgroundConfig[] }> = [];
  for (const [family, variants] of families.entries()) {
    variants.sort((a, b) => (a.variantIndex ?? 1) - (b.variantIndex ?? 1));
    out.push({ family, name: variants[0]?.name ?? family, variants });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/** Find the first background variant for a given family slug. */
export function getDefaultBackgroundForFamily(
  family: string
): AvatarBackgroundConfig | undefined {
  const variants = BACKGROUND_REGISTRY
    .filter(bg => bg.family === family && bg.ready !== false)
    .sort((a, b) => (a.variantIndex ?? 1) - (b.variantIndex ?? 1));
  const first = variants[0];
  return first ? resolveBackgroundAssets(first) : undefined;
}

/** Get the default generated background used when no saved background is selected. */
export function getDefaultBackground(): AvatarBackgroundConfig {
  return (
    getBackgroundById('service-lounge-v1') ??
    getDefaultBackgroundForFamily('service-lounge') ??
    resolveBackgroundAssets(BACKGROUND_REGISTRY[0])
  );
}

/** Get the default generated background for a specific avatar model. */
export function getDefaultBackgroundForModel(modelId?: string): AvatarBackgroundConfig {
  if (modelId) {
    const modelBackground = BACKGROUND_REGISTRY.find(background =>
      background.ready !== false && background.defaultForModelIds?.includes(modelId)
    );
    if (modelBackground) {
      return resolveBackgroundAssets(modelBackground);
    }
  }

  return getDefaultBackground();
}

/** Resolve the best background image URL for the current viewport. */
export function resolveBackgroundUrlForViewport(
  background: AvatarBackgroundConfig,
  viewport: { width: number; height: number } | null = null
): string {
  const variants = background.variants ?? {};
  const isPortrait = viewport ? viewport.height > viewport.width : false;
  const width = viewport?.width ?? 1920;
  const selected =
    isPortrait
      ? variants.mobile
      : width <= 768
        ? variants.mobile
        : width <= 1280
          ? variants.tablet
          : variants.desktop;

  return selected ?? variants.desktop ?? background.url;
}

/** Default avatar model ID used when no explicit model is selected */
export const DEFAULT_AVATAR_MODEL_ID = 'default';

/** Get the platform default model (Arc - male) */
export function getDefaultModel(): ModelConfig {
  return getModelById(DEFAULT_AVATAR_MODEL_ID) ?? MODEL_REGISTRY[0];
}
